/**
 * MemoryStore - v13 Section 8.7
 *
 * LangGraph Store-compatible interface for memory operations.
 * Provides put, get, delete, list, and search operations with
 * hybrid search (semantic + BM25 + RRF fusion).
 *
 * @see docs/architecture/extracts/memory-types-8.4.md
 */
import { SemanticSearch } from './search/semantic';
import { BM25Search } from './search/bm25';
import { RRFusion } from './search/rrf';
/**
 * MemoryStore - LangGraph Store-compatible memory management
 */
export class MemoryStore {
    constructor(config) {
        this.backend = config.backend;
        this.embeddingService = config.embeddingService;
        this.onEvent = config.onEvent;
        this.semanticSearch = new SemanticSearch();
        this.bm25Search = new BM25Search();
        this.rrfFusion = new RRFusion();
    }
    /**
     * Store an item
     * @param namespace Namespace tuple [namespace, userId] or [namespace, userId, subkey]
     * @param key Item key
     * @param value Item value
     */
    async put(namespace, key, value) {
        const startTime = Date.now();
        // Auto-generate embedding if embeddingService is available and value has content
        let storedValue = value;
        const valueWithContent = value;
        if (this.embeddingService && valueWithContent.content && !valueWithContent.embedding) {
            const embedding = await this.embeddingService.embed(valueWithContent.content);
            storedValue = { ...value, embedding };
        }
        const [ns, userId] = namespace;
        await this.backend.put(ns, userId, key, storedValue);
        this.emitEvent({
            type: 'put',
            namespace,
            key,
            timestamp: Date.now(),
            durationMs: Date.now() - startTime,
        });
    }
    /**
     * Retrieve an item
     * @param namespace Namespace tuple
     * @param key Item key
     * @returns Item or null if not found
     */
    async get(namespace, key) {
        const startTime = Date.now();
        const [ns, userId] = namespace;
        const value = await this.backend.get(ns, userId, key);
        if (value) {
            // Update access tracking (only for items with accessCount - typically Memory types)
            let result = value;
            const valueAsRecord = value;
            if (typeof valueAsRecord === 'object' &&
                valueAsRecord !== null &&
                'accessCount' in valueAsRecord) {
                const updated = {
                    ...valueAsRecord,
                    lastAccessed: Date.now(),
                    accessCount: (valueAsRecord.accessCount ?? 0) + 1,
                };
                await this.backend.put(ns, userId, key, updated);
                result = updated;
            }
            this.emitEvent({
                type: 'get',
                namespace,
                key,
                timestamp: Date.now(),
                durationMs: Date.now() - startTime,
            });
            return result;
        }
        this.emitEvent({
            type: 'get',
            namespace,
            key,
            timestamp: Date.now(),
            durationMs: Date.now() - startTime,
        });
        return null;
    }
    /**
     * Delete an item
     * @param namespace Namespace tuple
     * @param key Item key
     * @returns true if deleted, false if not found
     */
    async delete(namespace, key) {
        const startTime = Date.now();
        const [ns, userId] = namespace;
        const deleted = await this.backend.delete(ns, userId, key);
        this.emitEvent({
            type: 'delete',
            namespace,
            key,
            timestamp: Date.now(),
            durationMs: Date.now() - startTime,
        });
        return deleted;
    }
    /**
     * List items in a namespace
     * @param namespace Namespace tuple
     * @param options List options (limit, offset, filter)
     * @returns List result with items and hasMore flag
     */
    async list(namespace, options) {
        const startTime = Date.now();
        const [ns, userId] = namespace;
        const limit = options?.limit ?? 100;
        const offset = options?.offset ?? 0;
        // Get one extra to check if there are more
        let items = await this.backend.list(ns, userId, limit + 1, offset);
        // Apply filter if provided
        if (options?.filter) {
            items = items.filter((item) => this.matchesFilter(item, options.filter));
        }
        const hasMore = items.length > limit;
        if (hasMore) {
            items = items.slice(0, limit);
        }
        this.emitEvent({
            type: 'list',
            namespace,
            timestamp: Date.now(),
            durationMs: Date.now() - startTime,
        });
        return { items, hasMore };
    }
    /**
     * Search items with hybrid search
     * @param query Search query options
     * @returns Scored search results
     */
    async search(query) {
        const startTime = Date.now();
        const [ns, userId] = query.namespace;
        // Load all items from namespace
        let items = await this.backend.list(ns, userId, 10000, 0);
        // Apply filter if provided
        if (query.filter) {
            items = items.filter((item) => this.matchesFilter(item, query.filter));
        }
        const limit = query.limit ?? 10;
        const resultLists = [];
        // BM25 search
        if (query.modes.includes('bm25')) {
            const bm25Results = this.bm25Search.search(items, query.query, limit * 2);
            resultLists.push(bm25Results);
        }
        // Semantic search
        if (query.modes.includes('semantic') && this.embeddingService) {
            const queryEmbedding = await this.embeddingService.embed(query.query);
            const semanticResults = this.semanticSearch.search(items, queryEmbedding, limit * 2);
            resultLists.push(semanticResults);
        }
        // Fuse results if we have multiple lists
        let results;
        if (resultLists.length === 0) {
            results = [];
        }
        else if (resultLists.length === 1) {
            // Single mode - use raw scores
            const single = resultLists[0];
            results = single.map((r) => ({
                item: r.item,
                scores: {
                    ...(query.modes.includes('bm25') ? { bm25: r.score } : {}),
                    ...(query.modes.includes('semantic') ? { semantic: r.score } : {}),
                    final: r.score,
                },
            }));
        }
        else {
            // Hybrid - fuse with RRF
            const fused = this.rrfFusion.fuse(resultLists, limit);
            results = fused.map((r) => ({
                item: r.item,
                scores: {
                    rrf: r.scores.rrf,
                    final: r.scores.final,
                },
            }));
        }
        // Apply additional scoring if weights provided
        if (query.weights) {
            results = this.applyWeightedScoring(results, query.weights);
        }
        // Sort by final score
        results.sort((a, b) => b.scores.final - a.scores.final);
        this.emitEvent({
            type: 'search',
            namespace: query.namespace,
            timestamp: Date.now(),
            durationMs: Date.now() - startTime,
        });
        return results.slice(0, limit);
    }
    /**
     * Check if an item matches the filter criteria
     */
    matchesFilter(item, filter) {
        if (!item || typeof item !== 'object')
            return false;
        for (const [key, value] of Object.entries(filter)) {
            if (item[key] !== value) {
                return false;
            }
        }
        return true;
    }
    /**
     * Apply weighted scoring for strength and recency
     */
    applyWeightedScoring(results, weights) {
        const now = Date.now();
        const oneDay = 86400000;
        return results.map((result) => {
            const memory = result.item;
            // Compute strength score (already 0-1)
            const strengthScore = memory.strength ?? 1.0;
            // Compute recency score (exponential decay)
            const age = now - (memory.createdAt ?? now);
            const recencyScore = Math.exp(-age / (7 * oneDay)); // Half-life of ~5 days
            // Compute weighted final score
            const rrfWeight = weights.rrf ?? 0.5;
            const strengthWeight = weights.strength ?? 0.3;
            const recencyWeight = weights.recency ?? 0.2;
            const baseScore = result.scores.rrf ?? result.scores.bm25 ?? result.scores.semantic ?? 0;
            const weightedFinal = baseScore * rrfWeight + strengthScore * strengthWeight + recencyScore * recencyWeight;
            return {
                ...result,
                scores: {
                    ...result.scores,
                    strength: strengthScore,
                    recency: recencyScore,
                    final: weightedFinal,
                },
            };
        });
    }
    /**
     * Emit a store event
     */
    emitEvent(event) {
        if (this.onEvent) {
            this.onEvent(event);
        }
    }
    /**
     * Close the store and backend
     */
    async close() {
        if (this.backend.close) {
            await this.backend.close();
        }
    }
}
